import { Link } from '@tanstack/react-router';
import {
  ArrowCounterClockwiseIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import type { ResourceLeaseHolder } from '@erp/rpc';
import { useReleaseRunLeases } from 'src/api/rpc/hooks/leaseable-resources/useLeaseableResources';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { useCanManageStaffSections } from '@/hooks/authz/useCanManageStaffSections';
import paths from 'src/paths';
import { cn } from 'src/lib/utils';
import {
  leaseHeldByFinishedRun,
  relativeTime,
  shortRunId,
} from './leaseHolders';

/**
 * Shared presentation for "who holds this resource right now". Two surfaces
 * render the same facts — the agent page's start-readiness notice and the
 * Settings › Agent resources list — so the wording, the "held by a finished
 * run" flag and the release action live here.
 */

/** Replaces nothing: added next to a resource whose slot a finished run still holds. */
export function HeldByFinishedRunBadge() {
  return (
    <Badge variant="warning" className="text-[10px] h-5 gap-1">
      <WarningCircleIcon className="h-3 w-3" weight="fill" />
      <T context="status badge for an agent resource whose lease is held by a run that already finished">
        Held by finished run
      </T>
    </Badge>
  );
}

function useRunStatusLabel() {
  const gt = useGT();
  return (status: ResourceLeaseHolder['runStatus']): string => {
    switch (status) {
      case 'running':
        return gt('running', { context: 'agent run status, mid-sentence' });
      case 'completed':
        return gt('finished', { context: 'agent run status, mid-sentence' });
      case 'failed':
        return gt('failed', { context: 'agent run status, mid-sentence' });
      case 'blocked':
        return gt('blocked', { context: 'agent run status, mid-sentence' });
      case null:
        return gt('deleted', {
          context:
            'agent run status when the run row no longer exists, mid-sentence',
        });
    }
  };
}

/**
 * Releases every lease a finished run still holds. Renders nothing for a
 * viewer who may not manage staff sections: the backend refuses them too,
 * and a button that only ever errors is worse than none.
 */
export function ReleaseRunLeasesButton({
  agentRunId,
  className,
}: {
  agentRunId: string;
  className?: string;
}) {
  const canManage = useCanManageStaffSections();
  const release = useReleaseRunLeases();
  if (!canManage) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      className={cn('h-8 shrink-0', className)}
      disabled={release.isPending}
      onClick={(event) => {
        event.stopPropagation();
        release.mutate({ agentRunId });
      }}
    >
      <ArrowCounterClockwiseIcon className="h-4 w-4 mr-1.5" />
      {release.isPending ? (
        <T context="button label while the leases a finished run held are being released">
          Releasing…
        </T>
      ) : (
        <T context="button to release the leases a finished run still holds on an agent resource">
          Release
        </T>
      )}
    </Button>
  );
}

function RunReference({ holder }: { holder: ResourceLeaseHolder }) {
  const label = (
    <span className="font-mono">{shortRunId(holder.agentRunId)}</span>
  );
  if (!holder.agentDefinitionId) return label;
  return (
    <Link
      to={paths.authenticated.backgroundAgents.definitionSessionDetail}
      params={{
        agentDefinitionId: holder.agentDefinitionId,
        agentRunId: holder.agentRunId,
      }}
      className="underline"
    >
      {label}
    </Link>
  );
}

/**
 * One lease, in a sentence: which agent's run holds it, what state that run
 * is in, and when the lease expires on its own. A finished holder gets the
 * release button; a live one is simply a competitor at work.
 */
export function LeaseHolderLine({
  holder,
  className,
}: {
  holder: ResourceLeaseHolder;
  className?: string;
}) {
  const statusLabel = useRunStatusLabel()(holder.runStatus);
  const finished = leaseHeldByFinishedRun(holder);
  const agentName = holder.agentName ?? (
    <T context="placeholder when the agent holding a resource lease has no name">
      an unknown agent
    </T>
  );
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs',
        finished
          ? 'text-amber-800 dark:text-amber-300'
          : 'text-muted-foreground',
        className,
      )}
    >
      <span>
        {finished && holder.runCompletedAt ? (
          <T context="line naming the run that holds an agent resource lease even though the run already finished">
            Held by <Var>{agentName}</Var> run <RunReference holder={holder} />,
            which <Var>{statusLabel}</Var>{' '}
            <Var>{relativeTime(holder.runCompletedAt)}</Var> &middot; lease
            expires <Var>{relativeTime(holder.expiresAt)}</Var>
          </T>
        ) : finished ? (
          <T context="line naming the run that holds an agent resource lease even though the run is gone or finished at an unknown time">
            Held by <Var>{agentName}</Var> run <RunReference holder={holder} />,
            which is <Var>{statusLabel}</Var> &middot; lease expires{' '}
            <Var>{relativeTime(holder.expiresAt)}</Var>
          </T>
        ) : (
          <T context="line naming the live run that currently holds an agent resource lease">
            In use by <Var>{agentName}</Var> run{' '}
            <RunReference holder={holder} /> since{' '}
            <Var>{relativeTime(holder.leasedAt)}</Var>
          </T>
        )}
      </span>
      {finished && <ReleaseRunLeasesButton agentRunId={holder.agentRunId} />}
    </div>
  );
}
